'use client'

import { cn } from '@brew/ui/cn'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { DeferredPanelBoundary } from '@/components/layout/deferred-panel-boundary'
import {
  BREW_LOGO_BOX_CLASSNAME,
  BREW_LOGO_MARK_CLASSNAME,
} from './brew-logo-chrome'

export type BrewAnimatedLogoProps = {
  className?: string
  /**
   * Whether the still mark is painted while the animation loads (the server
   * HTML, the chunk, the `.riv`, the first frame). On by default: a page
   * whose loading frame already shows the still hands off to the animation
   * without a blank. Off, the box stays empty until the mark draws itself
   * in, and the still is a fallback only — for a logo that is the first
   * thing on screen with nothing before it, like the public navbar.
   */
  stillWhileLoading?: boolean
}

const loadRive = () =>
  import('./brew-animated-logo-rive').then(
    (module) => module.BrewAnimatedLogoRive
  )

/** The same chunk, with and without the still as its loading fallback. */
const BrewAnimatedLogoRiveWithStill = dynamic(loadRive, {
  ssr: false,
  loading: () => <BrewStaticLogo />,
})
const BrewAnimatedLogoRiveBare = dynamic(loadRive, {
  ssr: false,
  loading: () => null,
})

/**
 * Takes no size of its own — the wrapper in `BrewAnimatedLogo` owns the box.
 * That is deliberate: handing a variant its own dimensions is what made the
 * logo shift, so it is not expressible here. It sits in the rectangle the
 * Rive artboard draws the mark in (`BREW_LOGO_MARK_CLASSNAME`), so it is
 * the same mark at the same size, not a larger one that then shrinks.
 */
function BrewStaticLogo() {
  return (
    <Image
      src="/logo.svg"
      alt="Brew"
      width={2963}
      height={1015}
      className={BREW_LOGO_MARK_CLASSNAME}
    />
  )
}

/**
 * The wordmark with no renderer: the same sized wrapper, the still image
 * inside it, and no `next/dynamic` chunk.
 *
 * `loading.tsx` shells paint this. A shell that mounted `BrewAnimatedLogo`
 * would fetch and initialize a Rive instance on motion-enabled clients only to
 * discard it moments later when the real hero replaced the shell.
 */
export function BrewStaticWordmark({ className }: BrewAnimatedLogoProps) {
  return (
    <span data-brew-logo="" className={cn(BREW_LOGO_BOX_CLASSNAME, className)}>
      <BrewStaticLogo />
    </span>
  )
}

type MotionPreference = 'unknown' | 'reduce' | 'allow'

/**
 * `unknown` on the server and through hydration, then the visitor's
 * preference in one update. A boolean with a server default would paint
 * one variant for a frame and then the other; this never shows a wrong one.
 */
function useMotionPreference(): MotionPreference {
  const [preference, setPreference] = useState<MotionPreference>('unknown')
  useEffect(() => {
    const query = matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setPreference(query.matches ? 'reduce' : 'allow')
    apply()
    query.addEventListener('change', apply)
    return () => query.removeEventListener('change', apply)
  }, [])
  return preference
}

/**
 * Horizontal Brew wordmark — same Rive asset as the signed-in home hero.
 *
 * The still mark shows for reduced motion, when the Rive chunk or file
 * fails to load, and for a visitor without JavaScript (`<noscript>`); with
 * `stillWhileLoading` it also holds the box until the animation has drawn
 * its first frame. Which variant shows has no layout consequence, because
 * the wrapper holds the box.
 */
export function BrewAnimatedLogo({
  className,
  stillWhileLoading = true,
}: BrewAnimatedLogoProps) {
  const motion = useMotionPreference()
  const [riveFailed, setRiveFailed] = useState(false)
  const onLoadError = useCallback(() => setRiveFailed(true), [])

  let mark: ReactNode
  if (motion === 'reduce' || riveFailed) {
    mark = <BrewStaticLogo />
  } else if (motion === 'unknown') {
    mark = stillWhileLoading ? (
      <BrewStaticLogo />
    ) : (
      <noscript>
        <BrewStaticLogo />
      </noscript>
    )
  } else {
    const Rive = stillWhileLoading
      ? BrewAnimatedLogoRiveWithStill
      : BrewAnimatedLogoRiveBare
    mark = (
      // The still mark also stands in when the Rive chunk itself fails to
      // load (the `.riv` failing is `onLoadError`).
      <DeferredPanelBoundary
        panelName="brew-animated-logo"
        fallback={<BrewStaticLogo />}
      >
        <Rive
          still={stillWhileLoading ? <BrewStaticLogo /> : null}
          onLoadError={onLoadError}
        />
      </DeferredPanelBoundary>
    )
  }

  return (
    <span data-brew-logo="" className={cn(BREW_LOGO_BOX_CLASSNAME, className)}>
      {mark}
    </span>
  )
}
